// Sign-ins the server has already cleared (no staff code needed, or already
// verified). Private pages render at once for these instead of re-running the
// staff check on every navigation or reload. Only a hash of the token is kept,
// in memory and in this tab's sessionStorage.
//
// UX only: private APIs still enforce staff verification on every request,
// and the gate still asks the server once per page load, quietly.
const STORAGE_KEY = 'mediasfu.staffSession.cleared';
const cleared = new Set();
const checkedThisLoad = new Set();

// Two 32-bit FNV-style passes; enough to tell sign-ins apart, not reversible.
export function staffSessionKey(token) {
  let a = 0x811c9dc5, b = 0x9747b28c;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ c, 0x5bd1e995);
  }
  return (a >>> 0).toString(36) + (b >>> 0).toString(36);
}

export function wasCleared(token) {
  if (!token) return false;
  const key = staffSessionKey(token);
  if (cleared.has(key)) return true;
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY) === key) { cleared.add(key); return true; }
  } catch { /* storage blocked: memory only */ }
  return false;
}

export function markCleared(token) {
  const key = staffSessionKey(token);
  cleared.add(key);
  try { window.sessionStorage.setItem(STORAGE_KEY, key); } catch { /* memory only */ }
}

export function forgetCleared(token) {
  if (!token) return;
  const key = staffSessionKey(token);
  cleared.delete(key);
  checkedThisLoad.delete(key);
  try { if (window.sessionStorage.getItem(STORAGE_KEY) === key) window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* memory only */ }
}

// True the first time this page load asks about this sign-in.
export function firstCheckThisLoad(token) {
  const key = staffSessionKey(token);
  if (checkedThisLoad.has(key)) return false;
  checkedThisLoad.add(key);
  return true;
}

export function allowRecheck(token) {
  checkedThisLoad.delete(staffSessionKey(token));
}

// Tests: `keepStorage` simulates a reload (memory gone, tab storage kept).
export function resetStaffSessionMemory({ keepStorage = false } = {}) {
  cleared.clear();
  checkedThisLoad.clear();
  if (!keepStorage) try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* memory only */ }
}
